import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConversationRole } from '@evaltick/shared';
import { AgentService, BaseAPIClient } from '@/services/api';
import type { ResumableAssessment } from '@/services/api';
import { AnswerMode, useInterviewStore, type ConversationEntry } from '@/store/interviewStore';
import { useSessionToken } from './useSessionToken';
import { useSessionRoutes } from './useSessionRoutes';
import { isRevokedError, revokedError } from './useRevokedKickOut';

interface EnterAssessmentOptions {
  /** Earlier-sitting answers to seed the answer box with, keyed by item id — the retake path's "here's what you said last time". */
  priorAnswersByItemId?: Map<string, string>;
  defaultAnswerMode?: AnswerMode;
}

/**
 * Loads a sitting into the interview store and navigates to it. Shared by
 * every way in — Sessions' "Start Session" and history "Continue", and
 * SessionResults' "Try Again" — because all three can be handed a sitting that
 * is already part-answered: `createAssessment` returns the open sitting for a
 * template rather than opening a second one, so even "start" and "retake" are
 * resume paths in disguise. A brand-new sitting lands on question 0 with
 * nothing locked; a part-answered one lands on its last answered question,
 * rehydrated and with the earlier ones locked.
 */
export const useEnterAssessment = () => {
  const navigate = useNavigate();
  const { getToken } = useSessionToken();
  const sessionRoutes = useSessionRoutes();
  const { startSession } = useInterviewStore();

  return useCallback(
    async (assessment: ResumableAssessment, options: EnterAssessmentOptions = {}) => {
      const { priorAnswersByItemId, defaultAnswerMode } = options;

      // GET /assessments/:id still returns sittings on revoked templates (results
      // stay readable), so throw the 410 error callers already handle.
      if (assessment.interviewTemplate.revokedAt) {
        throw revokedError();
      }

      // Scope comes from the sitting the server returned, never from what was
      // requested: a retake selection is only applied while the sitting has no
      // answers yet (see createAssessment), so the two can differ.
      const allItems = assessment.interviewTemplate.items;
      const scopedIds = new Set(assessment.retakeItemIds);
      const items =
        assessment.retakeItemIds.length > 0
          ? allItems.filter((item) => scopedIds.has(item.id))
          : allItems;
      if (items.length === 0) {
        throw new Error('Selected questions are no longer part of this template');
      }

      // Empty for a brand-new sitting — nothing answered yet.
      const answersByItemId = new Map(
        (assessment.answeredQuestions ?? []).map((a) => [a.interviewTemplateItemId, a.answer])
      );

      let lastAnsweredIndex = -1;
      for (let i = items.length - 1; i >= 0; i--) {
        if (answersByItemId.has(items[i].id)) {
          lastAnsweredIndex = i;
          break;
        }
      }
      const startIndex = Math.max(lastAnsweredIndex, 0);
      const landingItem = items[startIndex];
      const landingAnswer = answersByItemId.get(landingItem.id);

      // Only the landing question renders its conversation. Fetched from the
      // server (which decides what's visible) rather than rebuilt from the
      // answer alone, since the candidate may have kept talking after scoring.
      let conversation: ConversationEntry[] | undefined;
      if (landingAnswer !== undefined) {
        try {
          const token = await getToken();
          if (!token) throw new Error('Not authenticated');
          const { turns } = await new AgentService(token).getConversation(
            assessment.id,
            landingItem.id
          );
          conversation = turns.map((turn) => ({
            role: turn.role,
            content: turn.text,
            isStreaming: false,
            occurredAtMs: new Date(turn.occurredAt).getTime(),
          }));
        } catch (err) {
          // A revoke landing after the check above still kicks out.
          if (isRevokedError(err)) throw err;
          console.warn('Failed to load conversation on resume:', BaseAPIClient.parseErrorMsg(err));
        }
        if (!conversation || conversation.length === 0) {
          conversation = [
            { role: ConversationRole.Candidate, content: landingAnswer, isStreaming: false },
          ];
        }
      }

      startSession({
        assessmentId: assessment.id,
        templateName: assessment.interviewTemplate.name,
        coachingEnabled: assessment.interviewTemplate.coachingEnabled,
        questions: items.map((item) => ({
          id: item.id,
          text: item.questionText,
          competencyId: item.competencyId,
          questionType: item.questionType,
          // Present (substitution-aware) on the create/fetch-by-id payloads
          // this hook is always handed — see applySubstitutions in the API.
          questionChanged: item.questionChanged,
        })),
        priorAnswers: priorAnswersByItemId
          ? items.map((item) =>
              item.questionChanged ? '' : (priorAnswersByItemId.get(item.id) ?? '')
            )
          : undefined,
        defaultAnswerMode,
        resumeFrom: {
          index: startIndex,
          answeredQuestions: items.map((item) => answersByItemId.has(item.id)),
          conversation,
        },
      });

      navigate(sessionRoutes.interview(assessment.id));
    },
    [getToken, navigate, sessionRoutes, startSession]
  );
};
